import { EnPrint } from '@meronex/icons/en';
import { IconType, IconBaseProps } from '@meronex/icons';
import {
  BiExitFullscreen,
  BiFullscreen,
  BisDownArrow,
  BisHome,
  BisLeftArrow,
  BisRightArrow,
  BisUpArrow,
  BisUserCircle,
} from '@meronex/icons/bi';
import {
  FaBars,
  FaCopy,
  FaCut,
  FaDownload,
  FaEdit,
  FaExternalLinkAlt,
  FaFastBackward,
  FaFastForward,
  FaFile,
  FaFilter,
  FaFolder,
  FaFolderOpen,
  FaInfoCircle,
  FaMinus,
  FaMinusSquare,
  FaPaste,
  FaPlus,
  FaPlusSquare,
  FaQuestionCircle,
  FaSquare,
  FaStepBackward,
  FaStepForward,
  FaStar,
  FaTrash,
  FaUpload,
} from '@meronex/icons/fa';
import { FdAlert } from '@meronex/icons/fd';
import { FiSquare } from '@meronex/icons/fi';
import { HiOutlineTranslate } from '@meronex/icons/hi';
import {
  MdcCheckBold,
  MdcCloseThick,
  MdcFilter,
  MdcFilterOff,
  MdcFilterRemove,
  MdcFormatLetterEndsWith,
  MdcRefresh,
} from '@meronex/icons/mdc';
import { RiGroupFill, RiUser3Fill } from '@meronex/icons/ri';
import React from 'react';
import {
  AiFillBell,
  AiOutlineDrag,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from '@meronex/icons/ai';
import { GrDocument } from '@meronex/icons/gr';
import { GoSearch } from '@meronex/icons/go';
import { TRequireOnlyOne } from '../../types';

export type TIconType = IconType;

/**
 * La idea original al menos, es que los nombres de los íconos sean lo más
 * genérico posible. Como ejemplo, tenemos el ícono Plus, cuyo nombre no indica
 * nada acerca del rol que ocupa en la aplicación, sino que solamente describe
 * qué ícono podemos esperar encontrar.
 *
 * Para indicar qué uso podría tener en la aplicación, se pueden agregar tags
 * en el array tags correspondiente.
 */
export const IconsMap = {
  Alert: { icon: FdAlert, tags: ['warning'] },
  ArrowLeft: { icon: BisLeftArrow, tags: [] },
  ArrowRight: { icon: BisRightArrow, tags: [] },
  ArrowUp: { icon: BisUpArrow, tags: [] },
  ArrowDown: { icon: BisDownArrow, tags: [] },
  Bell: { icon: AiFillBell, tags: ['notification'] },
  Check: { icon: MdcCheckBold, tags: ['ok', 'success'] },
  Close: { icon: MdcCloseThick, tags: ['delete', 'erase', 'cross', 'error'] },
  Copy: { icon: FaCopy, tags: [] },
  Cut: { icon: FaCut, tags: [] },
  Download: { icon: FaDownload, tags: [] },
  DragAndDrop: { icon: AiOutlineDrag, tags: ['drag', 'drop', 'dnd'] },
  Edit: { icon: FaEdit, tags: ['pen'] },
  External: { icon: FaExternalLinkAlt, tags: ['open', 'link'] },
  File: { icon: FaFile, tags: ['document'] },
  Filter: { icon: FaFilter, tags: ['filter'] },
  Filter2: { icon: MdcFilter, tags: ['filter'] },
  NotFilter: { icon: MdcFilterOff, tags: ['filter'] },
  First: { icon: FaFastBackward, tags: [] },
  FolderClosed: { icon: FaFolder, tags: ['directory'] },
  FolderOpen: { icon: FaFolderOpen, tags: ['directory'] },
  Groups: { icon: RiGroupFill, tags: [] },
  Hamburguer: { icon: FaBars, tags: ['menu'] },
  Help: { icon: FaQuestionCircle, tags: ['question'] },
  Home: { icon: BisHome, tags: ['house'] },
  Info: { icon: FaInfoCircle, tags: [] },
  Last: { icon: FaFastForward, tags: [] },
  Maximize: { icon: BiFullscreen, tags: ['fullscreen'] },
  Minimize: { icon: BiExitFullscreen, tags: ['fullscreen'] },
  Minus: { icon: FaMinus, tags: ['collapse', 'hide'] },
  MinusFilled: { icon: FaMinusSquare, tags: ['collapse', 'hide'] },
  Next: { icon: FaStepForward, tags: [] },
  New: { icon: GrDocument, tags: ['document'] },
  NotShow: { icon: AiOutlineEyeInvisible, tags: ['eye', 'view'] },
  Paste: { icon: FaPaste, tags: [] },
  Plus: { icon: FaPlus, tags: ['additional', 'expand', 'show'] },
  PlusFilled: { icon: FaPlusSquare, tags: ['additional', 'expand', 'show'] },
  Previous: { icon: FaStepBackward, tags: [] },
  Print: { icon: EnPrint, tags: [] },
  Refresh: { icon: MdcRefresh, tags: ['reload'] },
  Search: { icon: GoSearch, tags: [] },
  Show: { icon: AiOutlineEye, tags: ['eye', 'view'] },
  SquareFilled: { icon: FaSquare, tags: [] },
  SquareOutline: { icon: FiSquare, tags: [] },
  Star: { icon: FaStar, tags: ['favorite'] },
  Translate: { icon: HiOutlineTranslate, tags: ['language'] },
  Trash: { icon: FaTrash, tags: ['delete', 'erase'] },
  Upload: { icon: FaUpload, tags: [] },
  User: { icon: BisUserCircle, tags: [] },
  User2: { icon: RiUser3Fill, tags: [] },
  CrossFilter: { icon: MdcFilterRemove, tags: [] },
  LetterAutocomplete: { icon: MdcFormatLetterEndsWith, tags: [] },
};

export const icons = new Proxy(
  {},
  {
    get(_, prop): TIconType {
      return IconsMap[prop as keyof typeof IconsMap].icon;
    },
  },
) as unknown as Record<keyof typeof IconsMap, TIconType>;

export type TIconName = keyof typeof IconsMap;

/**
 * Se puede utilizar este type guard para aceptar argumentos en los componentes
 * que permitan pasar tanto un nombre de ícono como un ícono en sí.
 *
 * @example
 *
 * ({icon}: {icon: TIconName | TIconType}) => {
 *   const ActualIcon = React.useMemo(
 *     ()=> isIconName(icon) ? (() => <Icon name={icon} />) : icon
 *   , [])
 *   return <ActualIcon />
 * }
 */
export function isIconName(arg: TIconName | TIconType): arg is TIconName {
  return typeof arg === 'string';
}

type TIconArgs = TRequireOnlyOne<{
  icon: TIconName | TIconType;
  name: TIconName;
}>;

const Icon = ({ icon, name, size, ...props }: TIconArgs & IconBaseProps) => {
  const IconComponent =
    icon === undefined ? icons[name] : isIconName(icon) ? icons[icon] : icon;
  return (
    <IconComponent
      {...props}
      sx={React.useMemo(
        () => ({
          // This is necessary because the size could be a ThemeUI string and
          // not a explicit measure
          width: size,
          height: size,
        }),
        [size],
      )}
    />
  );
};

export default Icon;
